/** Browser IANA zone, e.g. `Europe/London` or `Asia/Calcutta`. */
export function getIanaTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Browser IANA — required on every PUT /availability/schedule. */
export function getAvailabilitySaveTimezone(): string {
  return getIanaTimeZone();
}

/** Saved schedule timezone from GET /availability/schedule, else browser. */
export function resolveNutritionistSchedulingTimezone(scheduleTimezone?: string): string {
  const fromSchedule = scheduleTimezone?.trim();
  if (fromSchedule) return fromSchedule;
  return getIanaTimeZone();
}

/** Fallback before schedule loads in nutritionist appointment UI. */
export function getAppointmentSchedulingTimezone(): string {
  return getIanaTimeZone();
}

const readZonedParts = (ms: number, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms));

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
};

/** Converts a wall-clock date/time in `timeZone` to a UTC ISO string. */
export function wallClockToUtcIso(dateYmd: string, timeHhmm: string, timeZone: string): string {
  const [year, month, day] = dateYmd.split('-').map(Number);
  const [hour, minute] = timeHhmm.split(':').map(Number);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !timeZone.trim()
  ) {
    return '';
  }

  let guess = Date.UTC(year, month - 1, day, hour, minute, 0);

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const zoned = readZonedParts(guess, timeZone);
    const desiredMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const actualMs = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
    const delta = desiredMs - actualMs;
    if (delta === 0) break;
    guess += delta;
  }

  return new Date(guess).toISOString();
}

/** Resolves wall-clock slot times to UTC ISO strings for POST/PUT /appointments. */
export function buildAppointmentUtcTimes(params: {
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  startTimeUtc?: string;
  endTimeUtc?: string;
}): { startTime: string; endTime: string } {
  const trimmedStartUtc = params.startTimeUtc?.trim();
  const trimmedEndUtc = params.endTimeUtc?.trim();
  if (trimmedStartUtc && trimmedEndUtc) {
    return { startTime: trimmedStartUtc, endTime: trimmedEndUtc };
  }

  const startTime =
    trimmedStartUtc || wallClockToUtcIso(params.date, params.startTime, params.timezone);
  const endTime =
    trimmedEndUtc || wallClockToUtcIso(params.date, params.endTime, params.timezone);

  return { startTime, endTime };
}

/** @deprecated Use {@link buildAppointmentUtcTimes} — returns the same shape under legacy keys. */
export function buildAppointmentUtcRange(params: Parameters<typeof buildAppointmentUtcTimes>[0]): {
  startTimeUtc: string;
  endTimeUtc: string;
} {
  const { startTime, endTime } = buildAppointmentUtcTimes(params);
  return { startTimeUtc: startTime, endTimeUtc: endTime };
}
